import React from "react";
import { Link } from "react-router-dom";
import { Check, X, Minus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { planRangeLabel } from "@/lib/plans";

const COLS = [
  { key: "gx", label: "GovernX", highlight: true },
  { key: "native", label: "Native admin panels", sub: "Salesforce Setup, Zoho dashboards, Oracle consoles" },
  { key: "ipaas", label: "Generic iPaaS tools", sub: "Mulesoft / Workato-style platforms" },
];

const ROWS = [
  {
    label: "Unified view across Salesforce + Zoho + Oracle",
    gx: { v: "yes" },
    native: { v: "no", note: "separate logins" },
    ipaas: { v: "partial", note: "connectors exist, governance isn't the focus" },
  },
  { label: "Predictive limit forecasting", gx: { v: "yes" }, native: { v: "no" }, ipaas: { v: "no" } },
  { label: "Governance health score", gx: { v: "yes" }, native: { v: "no" }, ipaas: { v: "no" } },
  { label: "AI co-pilot (EMIE)", gx: { v: "yes" }, native: { v: "no" }, ipaas: { v: "no" } },
  {
    label: "Setup time",
    gx: { v: "text", t: "Minutes" },
    native: { v: "text", t: "N/A (already there)" },
    ipaas: { v: "text", t: "Days to weeks" },
  },
  {
    label: "Pricing",
    gx: { v: "text", t: planRangeLabel() },
    native: { v: "text", t: "Free (built-in)" },
    ipaas: { v: "text", t: "Enterprise pricing (often $1000s/mo)" },
  },
  {
    label: "Built for",
    gx: { v: "text", t: "Teams running 2+ of Salesforce / Zoho / Oracle" },
    native: { v: "text", t: "Single-platform monitoring" },
    ipaas: { v: "text", t: "Large-scale enterprise integration" },
  },
];

function Cell({ cell }) {
  if (cell.v === "yes") {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400">
        <Check className="h-4 w-4 shrink-0" /> Yes
      </span>
    );
  }
  if (cell.v === "no") {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <X className="h-4 w-4 shrink-0 text-rose-500" /> No
        </span>
        {cell.note && <span className="text-xs text-muted-foreground">{cell.note}</span>}
      </span>
    );
  }
  if (cell.v === "partial") {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <span className="inline-flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
          <Minus className="h-4 w-4 shrink-0" /> Partial
        </span>
        {cell.note && <span className="text-xs text-muted-foreground">{cell.note}</span>}
      </span>
    );
  }
  return <span className="font-medium text-foreground">{cell.t}</span>;
}

export default function Compare() {
  return (
    <div data-testid="compare-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Compare</p>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            How GovernX compares
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            See how GovernX stacks up against native admin panels and generic integration platforms.
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[720px] border-collapse text-left" data-testid="compare-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-[28%] px-5 py-5 align-bottom text-sm font-semibold text-muted-foreground">
                    Capability
                  </th>
                  {COLS.map((c) => (
                    <th
                      key={c.key}
                      className={`px-5 py-5 align-bottom ${c.highlight ? "bg-primary/5" : ""}`}
                      data-testid={`compare-col-${c.key}`}
                    >
                      <div className="font-display text-lg font-bold tracking-tight text-foreground">{c.label}</div>
                      {c.sub && <div className="mt-1 text-xs font-normal text-muted-foreground">{c.sub}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, i) => (
                  <tr
                    key={row.label}
                    className={`border-b border-border last:border-b-0 ${i % 2 === 1 ? "bg-card/40" : ""}`}
                    data-testid={`compare-row-${i}`}
                  >
                    <td className="px-5 py-4 text-sm font-semibold text-foreground">{row.label}</td>
                    <td className="bg-primary/5 px-5 py-4 text-sm">
                      <Cell cell={row.gx} />
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <Cell cell={row.native} />
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <Cell cell={row.ipaas} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Why not native */}
          <div className="mt-12 max-w-3xl rounded-2xl border border-border bg-card/40 p-8" data-testid="compare-why-native">
            <h2 className="font-display text-2xl font-bold tracking-tight">Why not just use native dashboards?</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground">
              Native tools work perfectly well for single-platform monitoring — if you only run Salesforce, its
              Setup pages are plenty. But teams running multiple platforms end up checking three separate logins,
              each with its own limits, alerts and quirks, and no unified view or forecasting across them. That
              fragmentation — no cross-platform alerting, no predictive limits, no single health score — is exactly
              the gap GovernX closes.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="h-12 gap-2 px-6 font-semibold" data-testid="compare-cta">
                Start your free trial <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link
              to="/features"
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
              data-testid="compare-features-link"
            >
              Explore all features
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
